import { Grid, Card, Box, Button, Typography } from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import AddIcon from "@mui/icons-material/Add";
import { push } from "connected-react-router";
import { useListController, ListContextProvider, useListContext, useDeleteWithConfirmController } from "ra-core";
import { FC, useCallback, useState } from "react";
import { useDispatch } from "react-redux";
import { Content } from "@/components/content/Content";
import TextField from "@/components/field/TextField";
import { Datagrid } from "@/components/list/datagrid";
import Scrollbar from "@/components/Scrollbar";
import { OdResourcePageProps } from "@/types/resource";
import RowExpandDataProtectionRuleEdit from "@/components/securefiles/RowExpandDataProtectionRuleEdit";
import { useDefaultSavedHandlers } from "@/controller/defaultSavedHandlers";
import { ProtectionRuleDto } from "@/gen/api/securefile";
import Confirm from "@/components/widgets/Confirm";
import { usePushBreadcrumb } from "@/utils/usePushBreadcrumb";
import usePermitGuard from "@/authProvider/usePermitGuard";
import { MANAGE_FILE_CLASSIFICATION_AND_PROTECTION_RULES } from "@/constants";
import { FunctionField } from "@/components/field";
import { RecPatch } from "@/dataProvider/tools";


const pageMark = { label: 'File Protection Rules' }

const DataProtectionRules: FC<OdResourcePageProps> = (props) => {
  const listCtrl = useListController({ basePath: '', filter: {}, })
  usePushBreadcrumb(pageMark, { toMatch: '/secure-files/'})
  
  usePermitGuard([[MANAGE_FILE_CLASSIFICATION_AND_PROTECTION_RULES]])
  return (
    <ListContextProvider value={listCtrl}>
      <DataProtectionRulesBody {...props} />
    </ListContextProvider>
  )
}

const DataProtectionRulesBody: FC<OdResourcePageProps> = ({
  options, resource
}) => {
  const listCtx = useListContext()
  const { refetch } = listCtx
  const dispatch = useDispatch()

  const [states, setStates] = useState({
    recordToDel: null as ProtectionRuleDto|null,
  })

  const delHandlers = useDefaultSavedHandlers('Delete File Protection Rule')
  delHandlers.onSuccess.merge(useCallback(() => {
    refetch && refetch()
  }, [refetch]))

  const {
    open: delConfirmOpen,
    handleDialogOpen,
    handleDialogClose,
    handleDelete,
    loading: delSaving,
  } = useDeleteWithConfirmController({
    resource,
    record: states.recordToDel as any,
    mutationMode: 'pessimistic',
    ...delHandlers,
  });

  const setRuleToBeDeleted = useCallback((r:ProtectionRuleDto) => {
    setStates(v => ({...v, recordToDel: r}))
    handleDialogOpen({stopPropagation:()=>{}} as any)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Content pageName="File Protection Rules" resourceName="File Protection Rules" breadcrumbs={options?.breadcrumbs}
      mainActions={
        <Button
          color="primary"
          startIcon={<AddIcon fontSize="small" />}
          variant="contained"
          onClick={() => dispatch(push(`/${resource}/create`))}
        >
          Create Rule
        </Button>
      }
    >
      <Grid container spacing={4} sx={{ mt: 2 }} >
        <Grid item xs={12}>
          <Card>
            <Scrollbar options={{ suppressScrollY: true }}>
              <Box sx={{ minWidth: 1150 }}>
                <Datagrid
                  hasBulkActions={false} 
                  expand={<RowExpandDataProtectionRuleEdit setRuleToBeDeleted={setRuleToBeDeleted} />}
                  sx={{
                    '& th' : { py: 1, textTransform: 'uppercase' },
                    '& td' : { py: 2 },
                    '& span.MuiTypography-root.filename' : { maxWidth:400, wordBreak:'break-word' },
                  }}
                  {...listCtx}
                >
                  <TextField label="Name" source="displayName" sortable={false} />
                  <TextField label="Description" source="comment" sortable={false} />
                  <FunctionField label="Active" source="activeRule" sortable={false} 
                    render={(d?:ProtectionRuleDto&RecPatch) => {
                      return (
                        <Typography variant="body2" {...(d?.activeRule ? {children: "Active"} : {children: "Inactive", color: "error"})} />)
                    }}
                  />
                </Datagrid>
              </Box>
            </Scrollbar>
          </Card>
          <Confirm
            isOpen={delConfirmOpen}
            loading={delSaving}
            title={"Delete File Protection Rule"}
            onConfirm={handleDelete}
            onClose={(e:any) => {
              handleDialogClose(e)
              setStates(v => ({...v, recordToDel:null}))
            }}
            content={
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <InfoIcon fontSize="large" sx={{ color: 'info.main' }}/>
                <Typography
                  component="span"
                  variant="body2"
                  sx={{ ml: 1 }}
                >
                  Please confirm whether to proceed with the deletion of this
                  {` "${states.recordToDel?.displayName}" `}
                  File Protection Rule.
                </Typography>
              </Box>
            }
          />
        </Grid>
      </Grid>
    </Content>
  )
}

export default DataProtectionRules;
